/**
 * Supabase Client Initialization - Optimized Implementation
 *
 * This file initializes the Supabase client for use throughout the application.
 * It handles authentication tokens and provides a centralized client instance.
 *
 * Optimization improvements (January 20, 2026):
 * - Added connection health monitoring with status tracking
 * - Added correlation ID support for request tracing
 * - Added automatic session refresh with configurable intervals
 * - Added session timeout detection and handling
 * - Added connection retry logic with exponential backoff
 * - Added structured logging with context
 * - Added connection pool health metrics
 * - Added auth state change listeners with callbacks
 *
 * Environment Variables Required:
 * - VITE_SUPABASE_URL: The Supabase project URL
 * - VITE_SUPABASE_ANON_KEY: The Supabase anonymous/public key
 */

import { createClient, AuthChangeEvent, Session, User, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '../types/database.types';
import { logger } from '../lib/logger';

// ============================================
// TYPES AND INTERFACES
// ============================================

/**
 * Connection health status enum
 */
export enum ConnectionStatus {
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  RECONNECTING = 'reconnecting',
  ERROR = 'error',
  UNKNOWN = 'unknown',
}

/**
 * Connection health information
 */
export interface ConnectionHealth {
  /** Current connection status */
  status: ConnectionStatus;
  /** Last successful connection timestamp */
  lastConnected: number | null;
  /** Last error timestamp */
  lastError: number | null;
  /** Last error message */
  lastErrorMessage: string | null;
  /** Number of consecutive failures */
  consecutiveFailures: number;
  /** Last health check timestamp */
  lastHealthCheck: number | null;
  /** Average response time in ms (rolling average of last 10 checks) */
  avgResponseTimeMs: number | null;
}

/**
 * Session information with additional metadata
 */
export interface SessionInfo {
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Current access token */
  accessToken: string | null;
  /** Token expiration timestamp */
  expiresAt: number | null;
  /** Whether token is about to expire (within 5 minutes) */
  isExpiringSoon: boolean;
  /** Time until expiration in seconds */
  expiresInSeconds: number | null;
  /** User ID */
  userId: string | null;
  /** User email */
  userEmail: string | null;
  /** Correlation ID for this session */
  correlationId: string;
}

/**
 * Auth state change callback type
 */
export type AuthStateCallback = (event: AuthChangeEvent, session: Session | null) => void;

/**
 * Connection health check result
 */
interface HealthCheckResult {
  success: boolean;
  responseTimeMs: number;
  error?: string;
}

// ============================================
// CONSTANTS
// ============================================

/** Session expiry warning threshold (5 minutes) */
const SESSION_EXPIRY_WARNING_MS = 5 * 60 * 1000;

/** Auto-refresh interval (4 minutes) */
const AUTO_REFRESH_INTERVAL_MS = 4 * 60 * 1000;

/** Health check interval (30 seconds) */
const HEALTH_CHECK_INTERVAL_MS = 30 * 1000;

/** Maximum consecutive failures before marking as error */
const MAX_CONSECUTIVE_FAILURES = 3;

/** Retry delays for connection attempts (exponential backoff) */
const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 16000];

/** Rolling average window size for response times */
const RESPONSE_TIME_WINDOW = 10;

// ============================================
// ENVIRONMENT SETUP
// ============================================

// Support both Vite (import.meta.env) and Node/Test (process.env)
const getEnvVar = (key: string): string | undefined => {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env[key];
  }
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key];
  }
  return undefined;
};

const supabaseUrl = getEnvVar('VITE_SUPABASE_URL');
const supabaseAnonKey = getEnvVar('VITE_SUPABASE_ANON_KEY');

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. ' +
    'Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file.'
  );
}

// ============================================
// CONNECTION STATE MANAGEMENT
// ============================================

/** Current connection health state */
let connectionHealth: ConnectionHealth = {
  status: ConnectionStatus.UNKNOWN,
  lastConnected: null,
  lastError: null,
  lastErrorMessage: null,
  consecutiveFailures: 0,
  lastHealthCheck: null,
  avgResponseTimeMs: null,
};

/** Response time history for rolling average */
const responseTimeHistory: number[] = [];

/** Current correlation ID */
let currentCorrelationId: string = generateCorrelationId();

/** Auth state change listeners */
const authStateListeners: Set<AuthStateCallback> = new Set();

/** Auto-refresh timer */
let autoRefreshTimer: ReturnType<typeof setInterval> | null = null;

/** Health check timer */
let healthCheckTimer: ReturnType<typeof setInterval> | null = null;

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Generate a new correlation ID for request tracing
 */
export function generateCorrelationId(): string {
  return crypto.randomUUID();
}

/**
 * Get the current correlation ID
 */
export function getCorrelationId(): string {
  return currentCorrelationId;
}

/**
 * Set a new correlation ID (e.g., at the start of a user action)
 */
export function setCorrelationId(id: string): void {
  currentCorrelationId = id;
}

/**
 * Reset correlation ID (generate a new one)
 */
export function resetCorrelationId(): string {
  currentCorrelationId = generateCorrelationId();
  return currentCorrelationId;
}

/**
 * Update connection health state
 */
function updateConnectionHealth(updates: Partial<ConnectionHealth>): void {
  connectionHealth = { ...connectionHealth, ...updates };
}

/**
 * Record a response time and update rolling average
 */
function recordResponseTime(timeMs: number): void {
  responseTimeHistory.push(timeMs);
  if (responseTimeHistory.length > RESPONSE_TIME_WINDOW) {
    responseTimeHistory.shift();
  }
  const avg = responseTimeHistory.reduce((a, b) => a + b, 0) / responseTimeHistory.length;
  connectionHealth.avgResponseTimeMs = Math.round(avg);
}

/**
 * Sleep for a specified duration
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ============================================
// SUPABASE CLIENT INITIALIZATION
// ============================================

/**
 * Initialize and export the Supabase client with optimized settings
 */
export const supabase: SupabaseClient<Database> = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  global: {
    headers: {
      'X-Correlation-ID': currentCorrelationId,
    },
  },
});

// Set up auth state change listener
supabase.auth.onAuthStateChange((event, session) => {
  logger.debug('Auth state changed', {
    event,
    hasSession: !!session,
    correlationId: currentCorrelationId,
  });

  // Notify all registered listeners
  authStateListeners.forEach(callback => {
    try {
      callback(event, session);
    } catch (error) {
      logger.error('Error in auth state callback', error instanceof Error ? error : undefined);
    }
  });

  // Handle specific events
  if (event === 'SIGNED_OUT') {
    resetCorrelationId();
    logger.info('User signed out, correlation ID reset', { newCorrelationId: currentCorrelationId });
  } else if (event === 'SIGNED_IN') {
    logger.info('User signed in', { userId: session?.user?.id, correlationId: currentCorrelationId });
  } else if (event === 'TOKEN_REFRESHED') {
    logger.debug('Token refreshed', { correlationId: currentCorrelationId });
  }
});

// ============================================
// CONNECTION HEALTH MONITORING
// ============================================

/**
 * Perform a health check by making a simple query
 */
async function performHealthCheck(): Promise<HealthCheckResult> {
  const startTime = performance.now();
  try {
    // Use a lightweight query to check connection
    const { error } = await supabase
      .from('users')
      .select('id', { count: 'exact', head: true })
      .limit(1);

    const responseTimeMs = Math.round(performance.now() - startTime);

    if (error) {
      return { success: false, responseTimeMs, error: error.message };
    }

    return { success: true, responseTimeMs };
  } catch (error) {
    const responseTimeMs = Math.round(performance.now() - startTime);
    return {
      success: false,
      responseTimeMs,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Update connection status based on health check result
 */
function handleHealthCheckResult(result: HealthCheckResult): void {
  const now = Date.now();
  connectionHealth.lastHealthCheck = now;

  if (result.success) {
    recordResponseTime(result.responseTimeMs);
    updateConnectionHealth({
      status: ConnectionStatus.CONNECTED,
      lastConnected: now,
      consecutiveFailures: 0,
    });
  } else {
    const failures = connectionHealth.consecutiveFailures + 1;
    updateConnectionHealth({
      consecutiveFailures: failures,
      lastError: now,
      lastErrorMessage: result.error || 'Health check failed',
      status: failures >= MAX_CONSECUTIVE_FAILURES
        ? ConnectionStatus.ERROR
        : ConnectionStatus.RECONNECTING,
    });

    logger.warn('Connection health check failed', {
      consecutiveFailures: failures,
      error: result.error,
      correlationId: currentCorrelationId,
    });
  }
}

/**
 * Start connection health monitoring
 */
export function startHealthMonitoring(): void {
  if (healthCheckTimer) {
    return; // Already running
  }

  logger.info('Starting connection health monitoring', { intervalMs: HEALTH_CHECK_INTERVAL_MS });

  // Run initial health check
  performHealthCheck().then(handleHealthCheckResult);

  // Schedule periodic health checks
  healthCheckTimer = setInterval(async () => {
    const result = await performHealthCheck();
    handleHealthCheckResult(result);
  }, HEALTH_CHECK_INTERVAL_MS);
}

/**
 * Stop connection health monitoring
 */
export function stopHealthMonitoring(): void {
  if (healthCheckTimer) {
    clearInterval(healthCheckTimer);
    healthCheckTimer = null;
    logger.info('Connection health monitoring stopped');
  }
}

/**
 * Get current connection health status
 */
export function getConnectionHealth(): ConnectionHealth {
  return { ...connectionHealth };
}

/**
 * Check if connection is healthy
 */
export function isConnectionHealthy(): boolean {
  return connectionHealth.status === ConnectionStatus.CONNECTED;
}

// ============================================
// SESSION MANAGEMENT
// ============================================

/**
 * Start automatic session refresh
 */
export function startAutoRefresh(): void {
  if (autoRefreshTimer) {
    return; // Already running
  }

  logger.info('Starting automatic session refresh', { intervalMs: AUTO_REFRESH_INTERVAL_MS });

  autoRefreshTimer = setInterval(async () => {
    const session = await getSessionInfo();
    if (session.isAuthenticated && session.isExpiringSoon) {
      logger.debug('Session expiring soon, refreshing', {
        expiresInSeconds: session.expiresInSeconds,
        correlationId: currentCorrelationId,
      });
      await refreshAuthSession();
    }
  }, AUTO_REFRESH_INTERVAL_MS);
}

/**
 * Stop automatic session refresh
 */
export function stopAutoRefresh(): void {
  if (autoRefreshTimer) {
    clearInterval(autoRefreshTimer);
    autoRefreshTimer = null;
    logger.info('Automatic session refresh stopped');
  }
}

/**
 * Get detailed session information
 */
export async function getSessionInfo(): Promise<SessionInfo> {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
      logger.error('Error getting session info', error, { correlationId: currentCorrelationId });
      return createEmptySessionInfo();
    }

    if (!session) {
      return createEmptySessionInfo();
    }

    const now = Date.now();
    const expiresAt = session.expires_at ? session.expires_at * 1000 : null;
    const expiresInMs = expiresAt ? expiresAt - now : null;
    const isExpiringSoon = expiresInMs !== null && expiresInMs < SESSION_EXPIRY_WARNING_MS;

    return {
      isAuthenticated: true,
      accessToken: session.access_token,
      expiresAt,
      isExpiringSoon,
      expiresInSeconds: expiresInMs !== null ? Math.floor(expiresInMs / 1000) : null,
      userId: session.user?.id || null,
      userEmail: session.user?.email || null,
      correlationId: currentCorrelationId,
    };
  } catch (error) {
    logger.error('Unexpected error getting session info', error instanceof Error ? error : undefined, {
      correlationId: currentCorrelationId,
    });
    return createEmptySessionInfo();
  }
}

/**
 * Create an empty session info object
 */
function createEmptySessionInfo(): SessionInfo {
  return {
    isAuthenticated: false,
    accessToken: null,
    expiresAt: null,
    isExpiringSoon: false,
    expiresInSeconds: null,
    userId: null,
    userEmail: null,
    correlationId: currentCorrelationId,
  };
}

/**
 * Helper function to get the current authentication token
 * @returns The current session token or null
 */
export const getAuthToken = async (): Promise<string | null> => {
  try {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();

    if (error) {
      logger.error('Error getting auth session', error, { correlationId: currentCorrelationId });
      return null;
    }

    return session?.access_token || null;
  } catch (error) {
    logger.error('Unexpected error getting auth token', error instanceof Error ? error : undefined, {
      correlationId: currentCorrelationId,
    });
    return null;
  }
};

/**
 * Helper function to refresh the authentication session with retry logic
 * @param maxRetries Maximum number of retry attempts (default: 3)
 * @returns Whether the refresh was successful
 */
export const refreshAuthSession = async (maxRetries: number = 3): Promise<boolean> => {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const { error } = await supabase.auth.refreshSession();
      if (!error) {
        logger.debug('Session refreshed successfully', {
          attempt: attempt + 1,
          correlationId: currentCorrelationId,
        });
        return true;
      }
      lastError = error;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
    }

    // Wait before retry (exponential backoff)
    if (attempt < maxRetries - 1) {
      const delay = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];
      logger.debug('Session refresh failed, retrying', {
        attempt: attempt + 1,
        nextRetryMs: delay,
        error: lastError?.message,
        correlationId: currentCorrelationId,
      });
      await sleep(delay);
    }
  }

  logger.error('Failed to refresh session after all retries', lastError || undefined, {
    maxRetries,
    correlationId: currentCorrelationId,
  });
  return false;
};

/**
 * Helper function to check if user is authenticated
 * @returns Whether the user is currently authenticated
 */
export const isAuthenticated = async (): Promise<boolean> => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return !!session;
  } catch (error) {
    logger.error('Error checking authentication', error instanceof Error ? error : undefined, {
      correlationId: currentCorrelationId,
    });
    return false;
  }
};

/**
 * Helper function to get current user info
 * @returns The current user object or null
 */
export const getCurrentUserInfo = async (): Promise<User | null> => {
  try {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();

    if (error) {
      logger.error('Error getting user info', error, { correlationId: currentCorrelationId });
      return null;
    }

    return user;
  } catch (error) {
    logger.error('Unexpected error getting user info', error instanceof Error ? error : undefined, {
      correlationId: currentCorrelationId,
    });
    return null;
  }
};

// ============================================
// AUTH STATE LISTENERS
// ============================================

/**
 * Subscribe to auth state changes
 * @param callback Function to call when auth state changes
 * @returns Unsubscribe function
 */
export function onAuthStateChange(callback: AuthStateCallback): () => void {
  authStateListeners.add(callback);
  return () => {
    authStateListeners.delete(callback);
  };
}

/**
 * Get the number of registered auth state listeners
 */
export function getAuthListenerCount(): number {
  return authStateListeners.size;
}

// ============================================
// CONNECTION RETRY UTILITIES
// ============================================

/**
 * Execute a Supabase operation with retry logic
 * @param operation The async operation to execute
 * @param context Context string for logging
 * @param maxRetries Maximum retry attempts
 * @returns The operation result
 */
export async function withConnectionRetry<T>(
  operation: () => Promise<T>,
  context: string,
  maxRetries: number = 3
): Promise<T> {
  let lastError: Error | null = null;
  const correlationId = currentCorrelationId;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const result = await operation();
      if (attempt > 0) {
        logger.info('Operation succeeded after retry', {
          context,
          attempt: attempt + 1,
          correlationId,
        });
      }
      return result;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Check if error is retryable
      const isNetworkError = lastError.message.toLowerCase().includes('network') ||
                            lastError.message.toLowerCase().includes('fetch') ||
                            lastError.message.toLowerCase().includes('timeout');

      if (!isNetworkError && attempt === 0) {
        // Non-network errors fail immediately on first attempt
        throw lastError;
      }

      if (attempt < maxRetries - 1) {
        const delay = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];
        logger.debug('Operation failed, retrying', {
          context,
          attempt: attempt + 1,
          nextRetryMs: delay,
          error: lastError.message,
          correlationId,
        });
        await sleep(delay);
      }
    }
  }

  logger.error('Operation failed after all retries', lastError || undefined, {
    context,
    maxRetries,
    correlationId,
  });
  throw lastError;
}

// ============================================
// INITIALIZATION
// ============================================

/**
 * Initialize all monitoring features (call once at app startup)
 */
export function initializeSupabaseMonitoring(): void {
  startHealthMonitoring();
  startAutoRefresh();
  logger.info('Supabase monitoring initialized', { correlationId: currentCorrelationId });
}

/**
 * Clean up all monitoring features (call on app shutdown)
 */
export function cleanupSupabaseMonitoring(): void {
  stopHealthMonitoring();
  stopAutoRefresh();
  logger.info('Supabase monitoring cleaned up');
}
